import { motion } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useParallax } from "../../../hooks/useAnimations";
import { GlowButton, StaggerContainer, StaggerItem, FloatingElement } from "../../../components/animations/AnimatedElements";

const stats = [
  { num: "ADX", label: "통합 플랫폼", unit: "" },
  { num: "3", label: "핵심 기술 축", unit: "개", numeric: true },
  { num: "275", label: "AI 기본사회로 가는 파트너", unit: "개 역", numeric: true },
  { num: "2023", label: "창업 연도", unit: "년", numeric: true },
];

function useCountUp(end: number, duration: number = 2000, enabled: boolean = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!enabled) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [enabled, hasStarted]);

  useEffect(() => {
    if (!enabled || !hasStarted) return;

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      if (progress >= 1) {
        setCount(end);
        return;
      }

      const easedProgress = 1 - Math.pow(2, -10 * progress);
      setCount(Math.floor(easedProgress * end));

      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [duration, enabled, end, hasStarted]);

  return { count, ref };
}

function StatCard({ stat }: { stat: typeof stats[0]; index: number }) {
  const numericValue = Number.parseInt(stat.num, 10) || 0;
  const countResult = useCountUp(numericValue, 2000, Boolean(stat.numeric));

  return (
    <StaggerItem>
      <motion.div
        whileHover={{ 
          scale: 1.05, 
          borderColor: 'rgba(0, 229, 255, 0.4)',
          boxShadow: '0 0 20px rgba(0, 229, 255, 0.1)'
        }}
        className="border border-cyan-900/40 bg-brand-darkCard/60 backdrop-blur-sm rounded-lg px-5 py-4 transition-all"
      >
        <div className="text-2xl font-bold text-cyan-400">
          {stat.numeric ? (
            <span ref={countResult.ref}>
              {countResult.count}
            </span>
          ) : (
            stat.num
          )}
          {stat.unit && (
            <span className="text-sm font-normal text-cyan-300 ml-1">
              {stat.unit}
            </span>
          )}
        </div>
        <div className="text-gray-400 text-xs mt-1">{stat.label}</div>
      </motion.div>
    </StaggerItem>
  );
}

export default function HeroSection() {
  const { offset, ref: parallaxRef } = useParallax(0.3);

  const handleScroll = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut" as const,
      },
    },
  };

  return (
    <section
      ref={parallaxRef}
      id="hero"
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Parallax */}
      <div className="absolute inset-0 w-full h-full">
        <motion.img
          src="https://readdy.ai/api/search-image?query=dark%20futuristic%20smart%20city%20at%20night%20glowing%20cyan%20teal%20neon%20holographic%20neural%20network%20connections%20towering%20skyscrapers%20with%20digital%20data%20overlays%20surveillance%20cameras%20AI%20analysis%20deep%20navy%20black%20atmosphere%20cinematic%20ultra%20detailed%20photorealistic%20sci-fi%20technology%20infrastructure%20with%20glowing%20circuit%20patterns%20and%20light%20trails&width=1920&height=1080&seq=hero001&orientation=landscape"
          alt="CITY-AI 배경"
          className="w-full h-full object-cover object-top"
          style={{ transform: `translateY(${offset}px)` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark/90 via-brand-dark/60 to-brand-dark/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-transparent to-brand-dark/40" />
      </div>

      {/* Animated Grid Overlay */}
      <motion.div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,229,255,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(0,229,255,0.15) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
        animate={{
          backgroundPosition: ["0px 0px", "60px 60px"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      {/* Floating Glow Elements */}
      <FloatingElement className="absolute top-1/4 right-1/4 w-64 h-64 bg-cyan-500/10 rounded-full blur-3xl" amplitude={20} duration={8}>
        <div />
      </FloatingElement>
      <FloatingElement className="absolute bottom-1/3 left-1/5 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl" amplitude={15} duration={10}>
        <div />
      </FloatingElement>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-24 pb-20">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Accent Line & Label */}
          <motion.div variants={itemVariants} className="flex items-center gap-3 mb-6">
            <motion.span
              className="h-px bg-cyan-500"
              initial={{ width: 0 }}
              animate={{ width: 48 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            />
            <span className="text-cyan-400 text-xs tracking-[0.3em] uppercase font-medium">
              AI & Digital Twin ADX Platform
            </span>
          </motion.div>

          {/* Main Heading with Animation */}
          <motion.h1 variants={itemVariants} className="text-5xl/[1.25] lg:text-7xl/[1.25] font-bold text-white mb-6">
            <motion.span 
              className="block"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              AI와 Digital Twin의 경험이 녹아든
            </motion.span>
            <motion.span
              className="block"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-teal-400">
                자체 브랜드 ADX로
              </span>
            </motion.span>
            <motion.span
              className="block text-4xl lg:text-5xl text-gray-300 font-light mt-4"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
            >
              혁신적 비즈니스 파트너
            </motion.span>
          </motion.h1>

          {/* Description */}
          <motion.p
            variants={itemVariants}
            className="text-gray-300 text-lg leading-relaxed max-w-xl mb-10"
          >
            CITY-AI는 <strong className="text-cyan-400">ADX(AI & Digital Twin eXperience) 플랫폼</strong>을 기반으로
            고객 현장을 분석·컨설팅하고,<br className="hidden lg:block" />
            맞춤형 AI·디지털트윈 서비스와 솔루션을 구축해드리는 기업입니다.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div variants={itemVariants} className="flex flex-wrap gap-4">
            <GlowButton onClick={() => handleScroll("#products")}>
              제품·서비스 보기
            </GlowButton>
            <GlowButton variant="secondary" onClick={() => handleScroll("#contact")}>
              협력 문의하기
            </GlowButton>
          </motion.div>
        </motion.div>

        {/* Stats Grid */}
        <StaggerContainer className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-6" staggerDelay={0.15}>
          {stats.map((stat, index) => (
            <StatCard key={stat.label} stat={stat} index={index} />
          ))}
        </StaggerContainer>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        onClick={() => handleScroll("#about")}
        onKeyDown={(e) => e.key === 'Enter' && handleScroll("#about")}
        role="button"
        tabIndex={0}
        aria-label="아래로 스크롤"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 2, duration: 0.6 }}
      >
        <span className="text-gray-500 text-xs tracking-widest uppercase">Scroll</span>
        <motion.div
          className="w-px h-8 bg-gradient-to-b from-cyan-500 to-transparent"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
